import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, Loader2, PencilLine, RotateCcw, X } from 'lucide-react';
import { ChipsDestinosEmail } from './PropostaDestinos';
import type { DestinoProposta } from '@/lib/leadSmsApi';
import type { PreviaPropostaEmail } from '@/lib/propostaEmailApi';
import {
  MARCA_ABERTURA,
  MARCA_FECHAMENTO,
  MARCA_IMAGEM,
  ROTULO_METODO_EMAIL,
  LIMITE_ASSUNTO_EMAIL,
  type MetodoEnvioEmail,
} from '../../../../backend/src/services/leads/proposta-email-texto';

/**
 * PROPOSTA E-MAIL — a prévia É o editor (Douglas 05/10/2026).
 *
 * O e-mail aparece no lugar de sempre da prévia, montado pelo MESMO código que
 * envia (rota previa-proposta-email-lead). O consultor clica no texto e
 * escreve: a abertura (acima da imagem da proposta) e o fechamento (abaixo).
 * Imagem, valores, botão e assinatura não se digitam.
 *
 * O e-mail fica num iframe porque é HTML de e-mail (tabelas, estilo próprio):
 * dentro da tela o CSS do CRM o desmontaria e a prévia mentiria. O iframe não
 * roda script (CSP script-src 'none'); quem escuta o que é digitado é a tela.
 */

const TRILHO = 'inline-flex flex-shrink-0 gap-0.5 rounded-md bg-[#EDE8E0] p-0.5 dark:bg-[#161010]';
const OPCAO = 'h-6 rounded px-2.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:text-muted-foreground/60';
const OPCAO_ATIVA = 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]';
const OPCAO_LIVRE = 'text-foreground hover:bg-card';
const GHOST = 'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-border bg-card px-2 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-[#5B0A0D] hover:text-[#5B0A0D] dark:hover:border-[#C87484] dark:hover:text-[#C87484]';
const PRIMARIO = 'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-2.5 text-[11px] font-bold text-white dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]';
const CAMPO = 'w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] leading-relaxed text-foreground outline-none focus:border-[#B4808C]';

export type ModeloEmailProposta = 'previa' | 'natural';
const DICA_MODELO: Record<ModeloEmailProposta, string> = {
  previa: 'proposta em tabela · texto de cima e de baixo editáveis',
  natural: 'e-mail todo em texto · edita tudo',
};

const IMAGEM_GERANDO = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="560" height="160"><rect width="560" height="160" rx="8" fill="#F2EEE8"/><text x="280" y="86" text-anchor="middle" font-family="Arial" font-size="14" fill="#6B5A4D">gerando a imagem da proposta…</text></svg>',
)}`;

function escapar(x: string): string {
  return x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function montarDocumento(html: string, imagemUrl: string | null, abertura: string, fechamento: string, versao: number): string {
  const editavel = (campo: string, texto: string, vazio: string) =>
    `<div data-editavel="${campo}" data-vazio="${vazio}" contenteditable="true" spellcheck="true">${escapar(texto)}</div>`;
  const corpo = html
    .replace(MARCA_ABERTURA, editavel('abertura', abertura, 'Escreva a abertura do e-mail'))
    .replace(MARCA_FECHAMENTO, editavel('fechamento', fechamento, 'Escreva o fechamento (opcional)'))
    .split(MARCA_IMAGEM).join(imagemUrl ?? IMAGEM_GERANDO);
  return `<!doctype html><!-- v${versao} --><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="script-src 'none'">
<style>
body{margin:0;background:#f7f4f1}
[data-editavel]{white-space:pre-wrap;outline:none;border-radius:4px;margin:0 -6px 14px;padding:4px 6px;cursor:text;transition:background-color .15s}
[data-editavel]:hover{background:#F6F1EA}
[data-editavel]:focus{background:#FFFDF8;box-shadow:0 0 0 1.5px #B4808C}
[data-editavel]:empty::before{content:attr(data-vazio);color:#A6907B}
</style></head><body>${corpo}</body></html>`;
}

interface PreviaEditavelProps {
  html: string;
  imagemUrl: string | null;
  abertura: string;
  fechamento: string;
  onAbertura: (t: string) => void;
  onFechamento: (t: string) => void;
  /** Muda quando o texto volta ao pronto: o documento é remontado. */
  versao: number;
}

function PreviaEmailEditavel({ html, imagemUrl, abertura, fechamento, onAbertura, onFechamento, versao }: PreviaEditavelProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // O documento só é remontado quando muda o e-mail, a imagem ou a versão —
  // nunca a cada tecla, senão o cursor pularia para o começo.
  const textosRef = useRef({ abertura, fechamento });
  textosRef.current = { abertura, fechamento };
  const avisarRef = useRef({ onAbertura, onFechamento });
  avisarRef.current = { onAbertura, onFechamento };

  const documento = useMemo(
    () => montarDocumento(html, imagemUrl, textosRef.current.abertura, textosRef.current.fechamento, versao),
    [html, imagemUrl, versao],
  );

  const ajustarAltura = () => {
    const doc = iframeRef.current?.contentDocument;
    if (iframeRef.current && doc?.body) iframeRef.current.style.height = `${doc.body.scrollHeight}px`;
  };

  const aoCarregar = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.querySelectorAll<HTMLElement>('[data-editavel]').forEach((el) => {
      el.addEventListener('input', () => {
        const texto = el.innerText;
        if (el.dataset.editavel === 'abertura') avisarRef.current.onAbertura(texto);
        else avisarRef.current.onFechamento(texto);
        ajustarAltura();
      });
      // Colar entra como texto puro: o e-mail não leva a formatação de outro lugar.
      el.addEventListener('paste', (e) => {
        e.preventDefault();
        doc.execCommand('insertText', false, e.clipboardData?.getData('text/plain') ?? '');
      });
    });
    doc.querySelectorAll('a').forEach((a) => a.addEventListener('click', (e) => e.preventDefault()));
    doc.querySelectorAll('img').forEach((img) => img.addEventListener('load', ajustarAltura));
    ajustarAltura();
  };

  useEffect(() => {
    const aoRedimensionar = () => ajustarAltura();
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="Prévia do e-mail da proposta"
      srcDoc={documento}
      onLoad={aoCarregar}
      className="block w-full border-0"
      style={{ height: 480 }}
    />
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex min-h-[34px] items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
      <span className="w-14 flex-shrink-0 text-[11px] text-muted-foreground">{rotulo}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">{children}</div>
    </div>
  );
}

export interface PropostaEmailComposerProps {
  previa: PreviaPropostaEmail | undefined;
  carregandoPrevia: boolean;
  erroPrevia: string | null;
  imagemUrl: string | null;
  metodo: MetodoEnvioEmail;
  onMetodo: (m: MetodoEnvioEmail) => void;
  destinos: DestinoProposta[];
  marcados: string[];
  onMarcados: (m: string[]) => void;
  carregandoDestinos: boolean;
  erroDestinos: string | null;
  assunto: string;
  onAssunto: (t: string) => void;
  abertura: string;
  fechamento: string;
  onAbertura: (t: string) => void;
  onFechamento: (t: string) => void;
  versaoTexto: number;
  onVoltarTextoPronto: () => void;
  aviso: string | null;
  /** De onde vêm os valores: o resumo do montador ou a proposta do TOP. */
  origemValores?: string;
  /** Rodapé inteiro, quando o e-mail não tem botão nem valores travados (o Natural). */
  rodape?: string;
  /** Prévia (tabela da proposta) ou Natural (o e-mail inteiro em texto). Sem isso, não mostra a escolha. */
  modelo?: ModeloEmailProposta;
  onModelo?: (m: ModeloEmailProposta) => void;
}

export function PropostaEmailComposer(p: PropostaEmailComposerProps) {
  const smtp = p.previa?.smtp;
  const semSmtp = !!p.previa && !smtp?.disponivel;
  const nomeConsultor = p.previa?.consultor.nome || 'O consultor';

  const natural = p.modelo === 'natural';
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState({ abertura: '', fechamento: '' });
  const [versaoLocal, setVersaoLocal] = useState(0);
  const [copiado, setCopiado] = useState(false);

  const abrirEdicao = () => {
    setRascunho({ abertura: p.abertura, fechamento: p.fechamento });
    setEditando(true);
  };
  const salvarEdicao = () => {
    p.onAbertura(rascunho.abertura);
    p.onFechamento(rascunho.fechamento);
    setVersaoLocal((v) => v + 1);
    setEditando(false);
  };
  const copiar = () => {
    const texto = [p.abertura, p.fechamento].filter((t) => t.trim()).join('\n\n');
    void navigator.clipboard?.writeText(texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    });
  };

  const comoSai = !p.previa
    ? ''
    : p.metodo === 'smtp'
      ? `sai da caixa ${smtp?.email} · fica nos enviados · as respostas voltam para essa caixa`
      : p.metodo === 'ambos'
        ? `o cliente recebe dois e-mails iguais: um pela caixa ${smtp?.email} e um por ${p.previa.sendgrid.from}`
        : `sai de ${p.previa.sendgrid.from} · as respostas vão para ${p.previa.consultor.email}`;

  return (
    <div className="space-y-2">
      <div className="flex flex-nowrap items-center justify-between gap-3">
        <p className="min-w-0 truncate text-[10px] font-bold uppercase tracking-wider text-muted-foreground">O que o cliente recebe</p>
        <div className="flex flex-nowrap items-center gap-1">
          {editando ? (
            <>
              <button type="button" onClick={() => setEditando(false)} className={GHOST}><X className="h-3 w-3" /> Cancelar</button>
              <button type="button" onClick={salvarEdicao} className={PRIMARIO}><Check className="h-3 w-3" /> Salvar texto</button>
            </>
          ) : (
            <>
              <button type="button" onClick={p.onVoltarTextoPronto} className={GHOST} title="Desfaz o que foi escrito e volta ao texto pronto do TOP">
                <RotateCcw className="h-3 w-3" /> Texto pronto
              </button>
              <button type="button" onClick={copiar} disabled={!p.previa} className={GHOST}>
                {copiado ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copiado ? 'Copiado' : 'Copiar'}
              </button>
              <button type="button" onClick={abrirEdicao} disabled={!p.previa} className={GHOST}><PencilLine className="h-3 w-3" /> Editar</button>
            </>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {p.modelo && p.onModelo && (
          <Linha rotulo="Modelo">
            <span className={TRILHO}>
              {(['previa', 'natural'] as const).map((m) => (
                <button key={m} type="button" onClick={() => { setEditando(false); p.onModelo?.(m); }} className={`${OPCAO} ${p.modelo === m ? OPCAO_ATIVA : OPCAO_LIVRE}`}>
                  {m === 'previa' ? 'Prévia' : 'Natural'}
                </button>
              ))}
            </span>
            <span className="min-w-0 truncate text-[10.5px] text-muted-foreground">{DICA_MODELO[p.modelo]}</span>
          </Linha>
        )}
        <Linha rotulo="De">
          <span className={TRILHO}>
            {(['smtp', 'sendgrid', 'ambos'] as const).map((m) => {
              const travado = m !== 'sendgrid' && semSmtp;
              return (
                <button
                  key={m}
                  type="button"
                  disabled={travado}
                  title={travado ? `${nomeConsultor} não tem e-mail próprio (SMTP) cadastrado. Cadastre em Usuários.` : undefined}
                  onClick={() => p.onMetodo(m)}
                  className={`${OPCAO} ${p.metodo === m ? OPCAO_ATIVA : OPCAO_LIVRE}`}
                >
                  {m === 'ambos' ? 'Os dois' : ROTULO_METODO_EMAIL[m]}
                </button>
              );
            })}
          </span>
          <span className="min-w-0 truncate text-[10.5px] text-muted-foreground" title={comoSai}>
            {semSmtp ? `${nomeConsultor} sem SMTP cadastrado · só SendGrid` : comoSai}
          </span>
        </Linha>
        <Linha rotulo="Para">
          <ChipsDestinosEmail
            destinos={p.destinos}
            marcados={p.marcados}
            onChange={p.onMarcados}
            carregando={p.carregandoDestinos}
            erro={p.erroDestinos}
          />
        </Linha>
        <Linha rotulo="Cópia">
          {p.previa?.copia ? (
            <span className="truncate text-[11px]" title={p.previa.copia.email}>
              {p.previa.copia.nome || p.previa.copia.email} <span className="text-muted-foreground">· supervisor</span>
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">{p.previa ? 'sem cópia' : '—'}</span>
          )}
        </Linha>
        <Linha rotulo="Assunto">
          <input
            value={p.assunto}
            onChange={(e) => p.onAssunto(e.target.value)}
            maxLength={LIMITE_ASSUNTO_EMAIL}
            placeholder="Escreva o assunto"
            className="h-6 min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:ring-0"
          />
        </Linha>

        {editando && (
          <div className="border-b border-border bg-[#FAF8F4] dark:bg-[#241A1A]">
            <div className="flex h-[30px] items-center justify-between gap-3 px-3">
              <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-foreground">Editando o texto</p>
              <p className="min-w-0 truncate text-[10.5px] text-muted-foreground">
                {natural
                  ? 'o botão do WhatsApp e a sua assinatura entram embaixo ao enviar'
                  : p.modelo ? 'a tabela da proposta, o botão e a assinatura vêm do TOP' : 'a imagem da proposta, o botão e a assinatura não se digitam'}
              </p>
            </div>
            <div className="space-y-2 px-3 pb-3">
              {natural ? (
                <textarea value={rascunho.abertura} onChange={(e) => setRascunho({ ...rascunho, abertura: e.target.value })} rows={18} className={CAMPO} />
              ) : (
                <>
                  <label className="block text-[10.5px] font-semibold text-muted-foreground">Texto de cima, antes da proposta</label>
                  <textarea value={rascunho.abertura} onChange={(e) => setRascunho({ ...rascunho, abertura: e.target.value })} rows={6} className={CAMPO} />
                  <label className="block text-[10.5px] font-semibold text-muted-foreground">Texto de baixo, depois da proposta</label>
                  <textarea value={rascunho.fechamento} onChange={(e) => setRascunho({ ...rascunho, fechamento: e.target.value })} rows={3} className={CAMPO} />
                </>
              )}
            </div>
          </div>
        )}

        {p.previa ? (
          <PreviaEmailEditavel
            html={p.previa.html}
            imagemUrl={p.imagemUrl}
            abertura={p.abertura}
            fechamento={p.fechamento}
            onAbertura={p.onAbertura}
            onFechamento={p.onFechamento}
            versao={p.versaoTexto * 1000 + versaoLocal}
          />
        ) : (
          <div className="flex items-center gap-2 px-3 py-6 text-xs text-muted-foreground">
            {p.carregandoPrevia
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> montando o e-mail…</>
              : (p.erroPrevia || 'Monte a proposta para ver o e-mail.')}
          </div>
        )}
      </div>

      {p.aviso && (
        <p className="rounded-md bg-[repeating-linear-gradient(135deg,#F3E6E8_0_6px,#FAF1F2_6px_12px)] px-2.5 py-1.5 text-[11px] font-semibold text-[#5B0A0D] dark:bg-[repeating-linear-gradient(135deg,#2A1F1F_0_6px,#241A1A_6px_12px)] dark:text-[#C87484]">
          {p.aviso}
        </p>
      )}
      <p className="text-[10px] text-muted-foreground">
        {p.rodape ?? `${p.origemValores ?? 'a imagem e os valores vêm do resumo acima'} — nada digitado à mão · o botão "Falar no WhatsApp" leva ao dono do cliente, pela linha dele que estiver no ar`}
      </p>
    </div>
  );
}
